import React, {useEffect, useState} from "react";
import {DialogActions, DialogContent, DialogTitle, Divider} from "@mui/material";
import {Col, Row} from "reactstrap";
import Form from "../../base/form";
import Button from "../../base/button/button";
import Dialog from "../../base/dialog";
import MuiRadio from "../../base/radio";
import {RadioButtonChecked, RadioButtonUnchecked} from "@mui/icons-material";
import useIsMounted from "../../../hooks/use-is-mounted";
import Api from "../../../../core/services/api/api";
import LoadingLottieAnimation from "../../app-specific/lottie-animations/loading";
import Utils from "../../../../core/services/utils/utils";
import {OutlinedAutoCompleteForm} from "../../base/auto_complete/outlined_auto_complete";

const formKeys = {
    driver: 'driver'
}

const PartSupportDriverChecklistDialog = ({open, setOpen, requestId}) => {
    const [loading, setLoading] = useState(false);
    const [initialData, setInitialData] = useState({});
    const [initialCheckList, setInitialCheckList] = useState([]);
    const [driverKeyword, setDriverKeyword] = useState();
    const [loadingDrivers, setLoadingDrivers] = useState(false);
    const [drivers, setDrivers] = useState([]);
    const isMounted = useIsMounted();

    /**
     * Listens to the changes of the dialog being opened and closed
     * Calls the api for getting the driver's checklist
     */
    useEffect(() => {
        if (open) {
            getCurrentChecklist().then();
        }
    }, [open])

    /**
     * Calls the api for getting the current driver checklist template and data
     * @return {Promise<void>}
     */
    const getCurrentChecklist = async () => {
        setLoading(true);
        const response = await Api.getCurrentDriverCheckList(requestId);
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag) {
            setInitialData(response?.data ?? {});
            setInitialCheckList(response?.data?.checkLists?.map(e => ({
                name: e.title,
                value: response?.data?.values?.find(d => d.driverCheckListId === e.id)?.value
            })) ?? [])
        }
    }

    /**
     * Handles submission of driver's checklist
     */
    const onSubmit = async (values) => {
        setLoading(true);
        if (!Object.values(values).filter(e => !!e).length) {
            setOpen(false);
            setLoading(false);
            return;
        }
        const forApi = {
            driverId: values?.driver?.value?.id ?? initialData.driver.id ?? undefined,
            partSupportRequestId: requestId,
            values: Object.entries(values).map(([key, value]) => ({
                    checkListItemId: initialData.checkLists.find(t => t.title === key)?.id,
                    value: value,
                }
            )).slice(1).filter(e => !!e.value),
        }
        const response = await Api.updatePartSupportDriverCheckList(forApi);
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag) {
            setOpen(false);
        }
    }

    /**
     * Listens for the changes in driver keyword, and if keywords is not an empty string, sets a 300ms timer which at the
     * end of it will get all the drivers for the provided keywords.
     */
    useEffect(() => {
        if (!driverKeyword || !driverKeyword.length || !open)
            return;
        const timer = setTimeout(() => {
            if (!driverKeyword || !driverKeyword.length || !open)
                return;
            if (!isMounted()) return;
            getDrivers(driverKeyword).then();
        }, 300);
        return () => {
            clearTimeout(timer)
        };
    }, [driverKeyword])

    /**
     * Gets the list of drivers from the server based on the keyword entered by user
     * Sets the [drivers] in the inner state with the received option
     * @param keyword
     * @return {Promise<void>}
     */
    const getDrivers = async (keyword) => {
        setLoadingDrivers(true);
        await Utils.wait(400);
        const forApi = {
            keyword: keyword,
            pagination: {
                currentPage: 1,
                pageSize: 10,
            }
        }
        const response = await Api.getListOfDrivers(forApi)
        if (!isMounted())
            return;
        if (response?.resultFlag)
            setDrivers(response.data?.items ?? [])
        setLoadingDrivers(false);
    }

    /**
     * Render the auto complete options being shown in the popover based on their type
     * @param option
     * @return {JSX.Element}
     */
    const renderDriverOptions = (props, option) => {
        if (typeof option === 'string')
            return (
                <Row className='no-gutters mx-2  justify-content-between align-items-center'>
                    <p className='mx-2 p-2'>{option}</p>
                </Row>);
        return (
            <Row className='no-gutters justify-content-between align-items-center w-100'>
                <Col xs={3} className='text-center align-items-center justify-content-center'>
                    <p className='text-xs secondary-color-1 mx-2'>First Name</p>
                    <p className='text-xs mx-2 p-2'>{option.value?.firstname ?? '--'}</p>
                </Col>
                <Col xs={3} className='text-center align-items-center justify-content-center'>
                    <p className='text-xs secondary-color-1 mx-2'>Last Name</p>
                    <p className='text-xs mx-2 p-2'>{option.value?.lastname ?? '--'}</p>
                </Col>
                <Col xs={3} className='text-center align-items-center justify-content-center'>
                    <p className='text-xs secondary-color-1 mx-2 flex-wrap'>Email</p>
                    <p className='text-xxs mx-2 p-2'>{option.value?.email ?? '--'}</p>
                </Col>
                <Col xs={3} className='text-center align-items-center justify-content-center'>
                    <p className='text-xs secondary-color-1 mx-2'>Phone</p>
                    <p className='text-xs mx-2 p-2'>{option.value?.phone ?? '--'}</p>
                </Col>
            </Row>
        );
    }

    return (
        <>
            <Dialog
                open={open}
                setOpen={setOpen}
                maxWidth={'sm'}
                loading={loading}
                className={'bound-dialog part-support-driver-form-dialog'}
                scroll={'paper'}
                aria-labelledby="scroll-dialog-title"
            >
                <DialogTitle id={'scroll-dialog-title'}>
                    <p className={`text-lg text-color-1 my-3 font-weight-bold text-center`}>
                        Driver Form
                    </p>
                    <Divider className='divider'/>
                </DialogTitle>
                <DialogContent>
                    {
                        loading
                            ? <LoadingLottieAnimation width={'90%'}/>
                            :
                            <Col xs={12} className={'p-0 m-0'}>
                                <Form
                                    className='d-flex flex-wrap w-100 align-items-start'
                                    onSubmit={onSubmit}
                                    id={'form-id'}
                                    initialValues={{
                                        driver: !!initialData.driver ? `${initialData.driver.firstname} ${initialData.driver.lastname}` : null,
                                        ...(initialCheckList?.reduce((prev, current) => ({...prev, ...({[current.name]: current.value})}), {}) ?? {})
                                    }}
                                    render={({form, values}) => {
                                        return (
                                            <>
                                                <div className={'form px-2'}>
                                                    <Col xs={12} className={'mb-3 pt-2'}>
                                                        <OutlinedAutoCompleteForm
                                                            freeSolo={false}
                                                            renderOption={renderDriverOptions}
                                                            loading={loadingDrivers}
                                                            label={'Driver'}
                                                            placeholder={'driver...'}

                                                            name={formKeys.driver}
                                                            type={'text'}
                                                            required={false}
                                                            options={drivers?.map(driver => ({
                                                                title: `${driver.firstname ?? ''} ${driver.lastname ?? ''}`,
                                                                value: driver
                                                            } ?? []))}
                                                            selectOnFocus
                                                            value={values[formKeys.driver] ?? ''}
                                                            onKeywordsChange={(e) => setDriverKeyword(e.target.value)}
                                                        />
                                                    </Col>
                                                    <div className={'table-title'}>
                                                        <p className={'title'}>Driver Checklist</p>
                                                        <div className={'title-options'}>
                                                            <p>Yes</p>
                                                            <p>No</p>
                                                        </div>
                                                    </div>
                                                    {initialCheckList?.map(record => (
                                                        <div
                                                            key={record?.name}
                                                            className={'table-content'}
                                                        >
                                                            <p className={'content-title'}>{record?.name}</p>
                                                            <MuiRadio
                                                                form
                                                                required={true}
                                                                name={record?.name}
                                                                radioGroupProps={{row: true}}
                                                                color={'secondary'}
                                                                data={[
                                                                    {label: '', value: 'true',},
                                                                    {label: '', value: 'false',}
                                                                ]}
                                                                onClick={(e) => form.change(e.target.name, e.target.value)}
                                                                disabled={loading || values[formKeys.driver] === null}
                                                                icon={<RadioButtonUnchecked/>}
                                                                checkedIcon={<RadioButtonChecked/>}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                                <DialogActions className={'p-0 d-flex flex-row w-100'}>
                                                    <Row className={'w-100 no-gutters'}>
                                                        <Divider className='divider'/>
                                                        <Row className='no-gutters justify-content-between align-items-start w-100'>
                                                            <Button
                                                                disabled={!open || loading}
                                                                variant={'dialog'}
                                                                color={'primary'}
                                                                onClick={() => setOpen(false)}
                                                                borderRadius={'0 8px 0 8px'}
                                                            >
                                                                Close
                                                            </Button>
                                                            <Button
                                                                disabled={!open || loading || values[formKeys.driver] === null}
                                                                variant={'dialog'}
                                                                color={'primary'}
                                                                type={'submit'}
                                                                form={'form-id'}
                                                                borderRadius={'8px 0 8px 0'}
                                                            >
                                                                {
                                                                    loading
                                                                        ? "Loading..."
                                                                        : "Save"
                                                                }
                                                            </Button>
                                                        </Row>
                                                    </Row>
                                                </DialogActions>
                                            </>
                                        )
                                    }}
                                />
                            </Col>
                    }
                </DialogContent>

            </Dialog>
        </>
    );
}

export default PartSupportDriverChecklistDialog;
